import { fabric } from 'fabric';

/**
 * Applies stroke settings to an object with outside stroke rendering.
 * The stroke appears outside the object like CSS box-shadow / outline —
 * the object's fill size and position stay exactly the same.
 *
 * Technique: paintFirst:'stroke' with doubled strokeWidth. The fill is
 * drawn on top and covers the inner half of the stroke. We then
 * preserve the object's center point so it doesn't shift.
 */
export function applyStrokeSettings(
  obj: fabric.Object,
  stroke: string,
  strokeWidth: number
): void {
  const logicalWidth = strokeWidth || 0;
  const hasStroke = stroke && logicalWidth > 0;

  // Save center point before any changes
  const center = obj.getCenterPoint();

  // Store the logical (user-facing) stroke width
  (obj as any)._outsideStrokeWidth = logicalWidth;

  if (hasStroke) {
    obj.set({
      stroke: stroke,
      strokeWidth: logicalWidth * 2,
      paintFirst: 'stroke',
      strokeUniform: true,
      strokeLineJoin: 'miter',
      strokeLineCap: 'butt',
      strokeMiterLimit: 10,
    });
  } else {
    obj.set({
      stroke: stroke || '',
      strokeWidth: 0,
      paintFirst: 'fill',
      strokeUniform: true,
    });
  }

  // Restore center point so the object doesn't move
  obj.setPositionByOrigin(center, 'center', 'center');
  obj.setCoords();
}

/**
 * Configures outside stroke for an object (e.g. after loading from JSON).
 * Reads the current stroke/strokeWidth from the object.
 */
export function configureOutsideStroke(obj: fabric.Object): void {
  const stroke = obj.stroke as string;
  const logicalWidth = (obj as any)._outsideStrokeWidth ?? ((obj.strokeWidth as number) || 0);
  applyStrokeSettings(obj, stroke, logicalWidth);
}

/**
 * Returns the logical (user-facing) stroke width for an object.
 * The internal strokeWidth is doubled for outside-stroke rendering,
 * so this returns the original value the user set.
 */
export function getLogicalStrokeWidth(obj: fabric.Object): number {
  if ((obj as any)._outsideStrokeWidth != null) {
    return (obj as any)._outsideStrokeWidth;
  }
  return (obj.strokeWidth as number) || 0;
}
